import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Plan, CreatePlanInput, UpdatePlanInput } from "../types";

const PLAN_FIELDS = `
  id
  code
  name
  description
  monthlyPrice
  yearlyPrice
  currency
  usdMonthlyPrice
  usdYearlyPrice
  isActive
  limits {
    users
    products
    storageGB
    apiCallsPerMonth
  }
  createdAt
  updatedAt
`;

export const planApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlans: builder.query<Plan[], void>({
      query: () => ({
        body: {
          query: `
            query Plans {
              plans {
                ${PLAN_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<Plan[]>(res, "plans"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: "Plan" as const, id: p.id })),
              { type: "Plan" as const, id: "LIST" },
            ]
          : [{ type: "Plan" as const, id: "LIST" }],
    }),

    // Public — the services package.json version (for the sidebar footer).
    getServerVersion: builder.query<string, void>({
      query: () => ({
        body: {
          query: `query ServerVersion { serverVersion }`,
        },
      }),
      transformResponse: (res: unknown) => pick<string>(res, "serverVersion"),
    }),

    // Public (unauthenticated) — active plans for the marketing landing page.
    getPublicPlans: builder.query<Plan[], void>({
      query: () => ({
        body: {
          query: `
            query PublicPlans {
              publicPlans {
                ${PLAN_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<Plan[]>(res, "publicPlans"),

      providesTags: [{ type: "Plan", id: "PUBLIC" }],
    }),

    getPlan: builder.query<Plan, string>({
      query: (id) => ({
        body: {
          query: `
            query Plan($id: String!) {
              plan(id: $id) {
                ${PLAN_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Plan>(res, "plan"),

      providesTags: (_result, _err, id) => [{ type: "Plan", id }],
    }),

    createPlan: builder.mutation<Plan, CreatePlanInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreatePlan($input: CreatePlanInput!) {
              createPlan(input: $input) {
                ${PLAN_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Plan>(res, "createPlan"),

      invalidatesTags: [{ type: "Plan", id: "LIST" }],
    }),

    updatePlan: builder.mutation<Plan, { id: string; input: UpdatePlanInput }>({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdatePlan($id: String!, $input: UpdatePlanInput!) {
              updatePlan(id: $id, input: $input) {
                ${PLAN_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Plan>(res, "updatePlan"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Plan", id },
        { type: "Plan", id: "LIST" },
      ],
    }),

    deletePlan: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeletePlan($id: String!) {
              deletePlan(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deletePlan"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Plan", id },
        { type: "Plan", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useLazyGetPlansQuery,
  useGetServerVersionQuery,
  useGetPublicPlansQuery,
  useGetPlanQuery,
  useLazyGetPlanQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
} = planApi;
